/**
 * Warden — Agentic Assurance practice.
 *
 * These four engagements answer "what can this agent actually be made to do".
 * Every technique named here is one the Cerberus and Argus research already
 * covers; nothing is described that we would have to invent to deliver.
 */

import type { ServicePageContent } from "./types";

const PRACTICE = "Agentic Assurance";

const AGENT_READINESS: ServicePageContent = {
  path: "/warden/agent-readiness",
  practice: PRACTICE,
  title: "Agent Readiness Assessment",
  summary:
    "Establish what your agents can reach today, and whether that is defensible.",
  headline:
    "Before you scale agents, find out what the ones you already have can reach",
  lede:
    "A structured assessment of the agents running in your organization — the tools they hold, the data those tools expose, and the actions nobody scoped when the integration was added.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=agent-readiness" },
    { label: "Explore Cerberus", href: "/cerberus" },
  ],
  scope: {
    heading: "Most estates cannot answer the first question",
    body:
      "Agents arrive through developer tooling, SaaS features and copilots rather than through procurement, so the inventory is usually incomplete and the permissions are usually inherited from whoever built the first prototype.",
    points: [
      {
        title: "The agents are not in the CMDB",
        body: "An assistant wired into a ticketing system through an API key is a production integration that no asset register knows about.",
      },
      {
        title: "Permissions were set for convenience",
        body: "Tool credentials are typically scoped to what made the demo work, not to what the task requires.",
      },
      {
        title: "Nobody owns the blast radius",
        body: "The team that built the agent owns the feature; no one owns the question of what it could do if it were manipulated.",
      },
    ],
  },
  method: {
    heading: "How the assessment runs",
    lede: "Two to four weeks, depending on estate size, run alongside your teams rather than over them.",
    steps: [
      {
        title: "Discovery",
        body: "Interviews plus telemetry and code review to enumerate agents, frameworks, models, tools and the credentials each one holds.",
      },
      {
        title: "Reachability analysis",
        body: "For each agent, the set of actions it can take and the data it can read — including transitively, through the tools it can call.",
      },
      {
        title: "Exposure rating",
        body: "Where the lethal trifecta of private data access, untrusted input and an outbound channel co-occurs in a single agent.",
      },
      {
        title: "Prioritized roadmap",
        body: "What to constrain first, what to instrument, and what is safe to leave alone for now.",
      },
    ],
  },
  deliverables: {
    heading: "What you hold at the end",
    items: [
      "An inventory of agents, models, frameworks and tool integrations in use",
      "A reachability map per agent: data readable, actions available, credentials held",
      "Exposure findings rated by blast radius, with the specific tool call that creates each one",
      "A remediation roadmap sequenced by risk reduction per unit of engineering effort",
      "A read-out for the risk or board audience, in their language rather than ours",
    ],
  },
  products: {
    heading: "What we deploy against the findings",
    lede: "The assessment is product-neutral. These are what we use when you want the findings enforced rather than filed.",
    slugs: ["cerberus", "tracelock"],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "Agentic Red Teaming", href: "/warden/agentic-red-teaming" },
      { label: "Tool-Boundary Review", href: "/warden/tool-boundary-review" },
      { label: "AI Inventory & Discovery", href: "/warden/ai-inventory" },
    ],
  },
  close: {
    heading: "Start with the estate you have, not the one on the roadmap",
    body:
      "Most organizations discover in week one that the agents already in production are the ones worth worrying about.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=agent-readiness" },
      { label: "Secure Agentic AI", href: "/secure-agentic-ai" },
    ],
  },
};

const AGENTIC_RED_TEAMING: ServicePageContent = {
  path: "/warden/agentic-red-teaming",
  practice: PRACTICE,
  title: "Agentic Red Teaming",
  summary: "Attack your agents the way an adversary would, then prove the fix.",
  headline: "An agent that has never been attacked is an assumption, not a control",
  lede:
    "Adversarial testing of your live agents by the team that builds Argus — prompt injection, tool abuse, privilege chaining and exfiltration, run against the system as deployed rather than against a model in isolation.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=red-teaming" },
    { label: "Explore Argus", href: "/argus" },
  ],
  scope: {
    heading: "Model evaluations do not test your system",
    body:
      "A benchmark tells you how a model behaves on a prompt. It tells you nothing about what happens when your agent reads an attacker-controlled document and then calls a tool that has your credentials attached.",
    points: [
      {
        title: "Injection arrives as data",
        body: "The payload is in the ticket, the PDF, the web page or the calendar invite the agent was asked to summarize.",
      },
      {
        title: "The chain is the vulnerability",
        body: "Individually safe tools compose into an exfiltration path once one of them can be steered.",
      },
      {
        title: "Guardrails are tested by adversaries, not by unit tests",
        body: "A filter that passes your own examples is not evidence against an attacker who gets to choose the input.",
      },
    ],
  },
  method: {
    heading: "How the exercise runs",
    steps: [
      {
        title: "Rules of engagement",
        body: "Scope, target environment, safety boundaries and escalation path agreed in writing before anything is sent.",
      },
      {
        title: "Reconnaissance",
        body: "Map the agent's tools, memory, retrieval sources and trust assumptions — the surface an attacker would work from.",
      },
      {
        title: "Campaign execution",
        body: "Injection, tool abuse, privilege chaining, memory poisoning and exfiltration attempts, run adaptively rather than from a fixed checklist.",
      },
      {
        title: "Retest",
        body: "After remediation, the attacks that succeeded are replayed so the fix is demonstrated rather than asserted.",
      },
    ],
  },
  deliverables: {
    heading: "What you hold at the end",
    items: [
      "Reproducible attack traces for every finding, with the exact inputs used",
      "Findings rated by achieved impact, not by theoretical severity",
      "The specific boundary change that closes each one",
      "A retest report showing which attacks no longer succeed",
      "An executive read-out that distinguishes what was proven from what was inferred",
    ],
  },
  products: {
    heading: "What runs underneath",
    lede: "Argus is delivered as a Warden engagement rather than as software you install.",
    slugs: ["argus", "cerberus"],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "Tool-Boundary Review", href: "/warden/tool-boundary-review" },
      { label: "Memory & Provenance Audit", href: "/warden/provenance-audit" },
      { label: "Cerberus Runtime Deployment", href: "/warden/cerberus-deployment" },
    ],
  },
  close: {
    heading: "Find it in an exercise",
    body:
      "The alternative is finding it in an incident, where you also have to explain why nobody had looked.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=red-teaming" },
      { label: "Read the research", href: "/research" },
    ],
  },
};

const TOOL_BOUNDARY_REVIEW: ServicePageContent = {
  path: "/warden/tool-boundary-review",
  practice: PRACTICE,
  title: "Tool-Boundary Review",
  summary:
    "Right-size what each agent can reach, and make the limit enforceable.",
  headline: "The useful control on an agent is what it is allowed to do next",
  lede:
    "A focused engagement on the tool layer: every function your agents can call, the credentials behind it, and whether the restriction you believe is in place is actually enforced anywhere.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=tool-boundary" },
    { label: "Explore Cerberus", href: "/cerberus" },
  ],
  scope: {
    heading: "A documented restriction is not a restriction",
    body:
      "Most agent guardrails live in the system prompt, which is a request rather than a control. The boundary that survives an adversary is the one evaluated at the point the action is taken.",
    points: [
      {
        title: "Prompt-level rules are advisory",
        body: "An instruction not to send data externally is negotiable by anyone who can influence the context window.",
      },
      {
        title: "Credentials outlive their purpose",
        body: "The token issued for a prototype is usually still attached when the agent reaches production.",
      },
      {
        title: "Read and write are rarely separated",
        body: "An agent that needs to read a repository is commonly given a scope that also lets it write to one.",
      },
    ],
  },
  method: {
    heading: "How the review runs",
    steps: [
      {
        title: "Enumerate the tool surface",
        body: "Every callable function, its parameters, its credential and the systems it reaches.",
      },
      {
        title: "Compare intent to enforcement",
        body: "For each stated restriction, identify where it is actually evaluated — prompt, application code, gateway or nowhere.",
      },
      {
        title: "Design the constraint",
        body: "Least-privilege scopes, and runtime conditions for the calls that cannot simply be removed.",
      },
      {
        title: "Verify",
        body: "Attempt the actions that should now be impossible, and record the result.",
      },
    ],
  },
  deliverables: {
    heading: "What you hold at the end",
    items: [
      "A tool register: every function, its credential, its reach and its owner",
      "A gap list of restrictions that exist in documentation but not in enforcement",
      "Recommended scopes and runtime conditions, expressed as policy you can deploy",
      "Verification evidence for the constraints applied during the engagement",
    ],
  },
  products: {
    heading: "What enforces it",
    slugs: ["cerberus"],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "Agent Readiness Assessment", href: "/warden/agent-readiness" },
      { label: "Agentic Red Teaming", href: "/warden/agentic-red-teaming" },
      { label: "Cerberus Runtime Deployment", href: "/warden/cerberus-deployment" },
    ],
  },
  close: {
    heading: "Constrain the action, keep the capability",
    body:
      "The goal is not a smaller agent. It is an agent that can still do the work and cannot do the damage.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=tool-boundary" },
      { label: "Secure Agentic AI", href: "/secure-agentic-ai" },
    ],
  },
};

const PROVENANCE_AUDIT: ServicePageContent = {
  path: "/warden/provenance-audit",
  practice: PRACTICE,
  title: "Memory & Provenance Audit",
  summary:
    "Establish where an agent's beliefs came from, and whether they can be trusted.",
  headline: "An agent's memory is a database that accepts writes from strangers",
  lede:
    "An audit of what your agents remember, retrieve and treat as trusted — and whether a single poisoned document can shape decisions long after it was ingested.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=provenance" },
    { label: "Explore Mimir", href: "/mimir" },
  ],
  scope: {
    heading: "Retrieval turns yesterday's content into today's instruction",
    body:
      "Vector stores and long-lived agent memory are usually populated from sources with mixed trust, then read back without any record of where a given claim originated.",
    points: [
      {
        title: "Ingestion is rarely authenticated",
        body: "Content is indexed because it was reachable, not because it was vetted.",
      },
      {
        title: "Provenance is dropped at retrieval",
        body: "The chunk that reaches the model usually arrives without its origin, so the agent cannot weigh it.",
      },
      {
        title: "Poisoning persists",
        body: "Unlike a prompt injection, a poisoned memory keeps influencing behavior across sessions and users.",
      },
    ],
  },
  method: {
    heading: "How the audit runs",
    steps: [
      {
        title: "Map the corpus",
        body: "Every source feeding retrieval or memory, and the trust level each one actually warrants.",
      },
      {
        title: "Trace provenance",
        body: "Follow a sample of retrieved content back to origin and record where the lineage breaks.",
      },
      {
        title: "Test with seeded content",
        body: "Introduce marked benign content into the ingestion path and observe whether and how it later influences output.",
      },
      {
        title: "Recommend controls",
        body: "Source segregation, provenance retention at retrieval, and detection for the paths that cannot be closed.",
      },
    ],
  },
  deliverables: {
    heading: "What you hold at the end",
    items: [
      "A source map of everything feeding memory and retrieval, with trust classification",
      "Findings on where provenance is lost between ingestion and generation",
      "Seeded-content results showing which ingestion paths reach production answers",
      "A control plan covering segregation, provenance retention and detection",
    ],
  },
  products: {
    heading: "Related products",
    lede:
      "Mimir is the research line behind this work. It is in preview and not generally available, so the engagement delivers the audit and the controls, not a Mimir deployment.",
    slugs: ["mimir", "cerberus"],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "Agentic Red Teaming", href: "/warden/agentic-red-teaming" },
      { label: "Agent Readiness Assessment", href: "/warden/agent-readiness" },
    ],
  },
  close: {
    heading: "Know where the answer came from",
    body:
      "An agent that cannot tell you the origin of a claim cannot be governed on the strength of that claim.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=provenance" },
      { label: "Explore Mimir", href: "/mimir" },
    ],
  },
};

export const ASSURANCE_SERVICES: ServicePageContent[] = [
  AGENT_READINESS,
  AGENTIC_RED_TEAMING,
  TOOL_BOUNDARY_REVIEW,
  PROVENANCE_AUDIT,
];
